import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Zap,
  Code2,
  ArrowRight,
  Award,
  Users,
  Star,
  Target,
  Palette
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPageUrl } from "../utils/createPageUrl";
import "../styles/home.css";

function Home() {
  const { t } = useTranslation();
  const [activeService, setActiveService] = useState("tennis");
  const navigate = useNavigate();

  const hero = t("home.hero", { returnObjects: true }) || {};
  const why = t("home.why", { returnObjects: true }) || {};
  const bento = t("home.bento", { returnObjects: true }) || {};
  const cta = t("home.cta", { returnObjects: true }) || {};

  const services = {
    tennis: {
      title: hero.tennisTitle,
      color: "#4CAF50",
      tagline: hero.tennisTagline,
      description: hero.tennisDesc,
      icon: Zap,
      path: createPageUrl("TennisCoaching"),
      features: hero.tennisFeatures || []
    },
    code: {
      title: hero.codeTitle,
      color: "#2D8CFF",
      tagline: hero.codeTagline,
      description: hero.codeDesc,
      icon: Code2,
      path: createPageUrl("WebDevelopment"),
      features: hero.codeFeatures || []
    }
  };

  const currentService = services[activeService];
  const Icon = currentService.icon;

  const renderWhyIcon = (iconKey) => {
    switch (iconKey) {
      case "award":
        return <Award className="home__why-card-icon" />;
      case "users":
        return <Users className="home__why-card-icon" />;
      case "star":
        return <Star className="home__why-card-icon" />;
      default:
        return <Star className="home__why-card-icon" />;
    }
  };

  const tennisCard = bento.tennis || {};
  const webCard = bento.web || {};

  return (
    <div className="home">
      {/* HERO */}
      <section className="home__hero">
        <div className="home__hero-bg">
          <motion.div
            className="home__hero-gradient"
            animate={{
              background:
                activeService === "tennis"
                  ? "linear-gradient(135deg, #F7F9FA 0%, #E8F5E9 100%)"
                  : "linear-gradient(135deg, #F7F9FA 0%, #E3F2FD 100%)"
            }}
            transition={{ duration: 0.6 }}
          />
          <motion.div
            className="home__hero-circle home__hero-circle--top"
            style={{ backgroundColor: currentService.color }}
            animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
            transition={{ duration: 20, repeat: Infinity }}
          />
          <motion.div
            className="home__hero-circle home__hero-circle--bottom"
            style={{ backgroundColor: currentService.color }}
            animate={{ scale: [1, 1.3, 1], rotate: [0, -90, 0] }}
            transition={{ duration: 15, repeat: Infinity }}
          />
        </div>

        <div className="home__hero-inner">
          {/* Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="home__toggle"
          >
            <button
              type="button"
              onClick={() => setActiveService("tennis")}
              className={
                "home__toggle-btn" +
                (activeService === "tennis"
                  ? " home__toggle-btn--active-tennis"
                  : "")
              }
            >
              <Zap className="home__toggle-icon" />
              <span>{hero.serveToggleLabel}</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveService("code")}
              className={
                "home__toggle-btn" +
                (activeService === "code"
                  ? " home__toggle-btn--active-code"
                  : "")
              }
            >
              <Code2 className="home__toggle-icon" />
              <span>{hero.codeToggleLabel}</span>
            </button>
          </motion.div>

          {/* Dynamic hero content */}
          <motion.div
            key={activeService}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <motion.div
              className="home__hero-tagline"
              style={{ borderColor: currentService.color }}
            >
              <Icon
                className="home__hero-tagline-icon"
                style={{ color: currentService.color }}
              />
              <span
                className="home__hero-tagline-text"
                style={{ color: currentService.color }}
              >
                {currentService.tagline}
              </span>
            </motion.div>

            <h1 className="home__hero-title">{currentService.title}</h1>

            <p className="home__hero-subtitle">
              {currentService.description}
            </p>

            <div className="home__hero-features">
              {(currentService.features || []).map((feature) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="home__hero-feature-pill"
                >
                  {feature}
                </motion.div>
              ))}
            </div>

            <Link to={currentService.path}>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="home__hero-cta"
                style={{ backgroundColor: currentService.color }}
              >
                {hero.learnMore}
                <ArrowRight className="home__hero-cta-icon" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHY SECTION */}
      <section className="home__why">
        <div className="home__why-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home__section-header"
          >
            <h2 className="home__section-title">{why.title}</h2>
            <p className="home__section-subtitle">{why.subtitle}</p>
          </motion.div>

          <div className="home__why-grid">
            {(why.cards || []).map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="home__why-card"
              >
                <div className="home__why-card-icon-wrapper">
                  {renderWhyIcon(item.icon)}
                </div>
                <h3 className="home__why-card-title">{item.title}</h3>
                <p className="home__why-card-text">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BENTO SERVICES */}
      <section className="home__bento">
        <div className="home__bento-inner">
          <div className="home__bento-grid">
            {/* Tennis card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="home__bento-card home__bento-card--tennis"
              onClick={() => navigate(createPageUrl("TennisCoaching"))}
            >
              <div className="home__bento-card-bg-circle home__bento-card-bg-circle--tennis" />
              <div className="home__bento-content">
                <Zap className="home__bento-icon" />
                <h3 className="home__bento-title">{tennisCard.title}</h3>
                <p className="home__bento-text">{tennisCard.description}</p>
                <ul className="home__bento-list">
                  {(tennisCard.features || []).map((item) => (
                    <li key={item} className="home__bento-list-item">
                      <Target className="home__bento-list-icon" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="home__bento-cta">
                  {tennisCard.cta}
                  <ArrowRight className="home__bento-cta-icon" />
                </div>
              </div>
            </motion.div>

            {/* Web card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="home__bento-card home__bento-card--web"
              onClick={() => navigate(createPageUrl("WebDevelopment"))}
            >
              <div className="home__bento-card-bg-circle home__bento-card-bg-circle--web" />
              <div className="home__bento-content">
                <Code2 className="home__bento-icon" />
                <h3 className="home__bento-title">{webCard.title}</h3>
                <p className="home__bento-text">{webCard.description}</p>
                <ul className="home__bento-list">
                  {(webCard.features || []).map((item) => (
                    <li key={item} className="home__bento-list-item">
                      <Palette className="home__bento-list-icon" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="home__bento-cta">
                  {webCard.cta}
                  <ArrowRight className="home__bento-cta-icon" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="home__cta">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="home__cta-card"
        >
          <h2 className="home__cta-title">{cta.title}</h2>
          <p className="home__cta-subtitle">{cta.subtitle}</p>
          <div className="home__cta-buttons">
            <Link to={createPageUrl("TennisCoaching") + "#tennis-booking"}>
              <button
                type="button"
                className="home__cta-btn home__cta-btn--tennis"
              >
                <Zap className="home__cta-btn-icon" />
                {cta.tennisButton}
              </button>
            </Link>
            <Link to={createPageUrl("WebDevelopment") + "#webdev-contact"}>
              <button
                type="button"
                className="home__cta-btn home__cta-btn--web"
              >
                <Code2 className="home__cta-btn-icon" />
                {cta.webButton}
              </button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

export default Home;
